import { useEffect, useState, useRef, useCallback, useMemo } from "react"
import TextField from "@mui/material/TextField"
import CircularProgress from "@mui/material/CircularProgress"
import IconButton from "@mui/material/IconButton"
import RefreshIcon from "@mui/icons-material/Refresh"
import Autocomplete from "@mui/material/Autocomplete"
import Checkbox from "@mui/material/Checkbox"
import Chip from "@mui/material/Chip"
import Button from "@mui/material/Button"
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank"
import CheckBoxIcon from "@mui/icons-material/CheckBox"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import AppApi from "../utils/AppApi"
import moment from "moment"
import { Timeline } from "vis-timeline"
import { DataSet } from "vis-data"

const JOB_COLORS = {
  F: "#bbdefb",
  B: "#c8e6c9",
  S: "#ffe0b2",
  L: "#e1bee7",
}

const JOB_LABELS = {
  F: "Front Haul",
  B: "Back Haul",
  S: "Short Range",
  L: "Long Range",
  G: "Garage",
}

const GARAGE_COLOR = "#ef9a9a"

const CHIP_PALETTE = [
  { bg: "#e3f2fd", border: "#90caf9", text: "#1565c0" },
  { bg: "#fce4ec", border: "#f48fb1", text: "#880e4f" },
  { bg: "#e8f5e9", border: "#a5d6a7", text: "#1b5e20" },
  { bg: "#fff3e0", border: "#ffcc80", text: "#e65100" },
  { bg: "#f3e5f5", border: "#ce93d8", text: "#4a148c" },
  { bg: "#e0f7fa", border: "#80deea", text: "#006064" },
  { bg: "#fff8e1", border: "#ffe082", text: "#f57f17" },
  { bg: "#fbe9e7", border: "#ffab91", text: "#bf360c" },
  { bg: "#e8eaf6", border: "#9fa8da", text: "#1a237e" },
  { bg: "#f1f8e9", border: "#c5e1a5", text: "#33691e" },
]

const getTruckColor = (truckNo) => {
  let hash = 0
  for (let i = 0; i < truckNo.length; i++) {
    hash = truckNo.charCodeAt(i) + ((hash << 5) - hash)
  }
  return CHIP_PALETTE[Math.abs(hash) % CHIP_PALETTE.length]
}

const ReportTruckGantt = () => {
  const [data, setData] = useState([])
  const [value, setValue] = useState(new Date())
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTrucks, setSelectedTrucks] = useState([])
  const containerRef = useRef(null)
  const timelineRef = useRef(null)

  const getData = useCallback(async () => {
    setIsLoading(true)
    try {
      let result = await AppApi.post("/rpt_driver", {
        viewMonth: moment(value).format("YYMM"),
        includeOverlapping: true,
      })
      setIsLoading(false)
      setData(result.data)
    } catch (error) {
      setIsLoading(false)
      console.log(error.message)
    }
  }, [value])

  useEffect(() => {
    getData()
  }, [getData])

  useEffect(() => {
    setSelectedTrucks(data.map((d) => d.truck_no))
  }, [data])

  useEffect(() => {
    if (!containerRef.current || data.length === 0) return

    if (timelineRef.current) {
      timelineRef.current.destroy()
      timelineRef.current = null
    }

    const filteredData = data.filter((d) => selectedTrucks.includes(d.truck_no))

    const groups = new DataSet(
      filteredData.map((d) => ({ id: d.truck_no, content: d.truck_no }))
    )

    const historyItems = filteredData.flatMap((d) =>
      d.history.filter((h) => h.start_time).map((h, i) => {
        const start = new Date(h.start_time)
        const end = h.end_time ? new Date(h.end_time) : new Date()
        const durMin = moment(end).diff(moment(start), "minutes")
        const hrs = Math.floor(durMin / 60)
        const mins = durMin % 60
        const color = JOB_COLORS[h.job_type] || "#888"

        return {
          id: `${d.truck_no}-h-${i}`,
          group: d.truck_no,
          content: `<span style="font-size:11px;color:#000;font-weight:bold">${h.dn} [${h.job_type}]</span>`,
          start,
          end,
          type: "range",
          className: `job-${h.job_type}`,
          style: `background-color:${color};border-color:${color};`,
          title: [
            `<b>DN:</b> ${h.dn}`,
            `<b>Driver:</b> ${h.driver}`,
            `<b>Job:</b> ${JOB_LABELS[h.job_type] || h.job_type}`,
            `<b>Start:</b> ${moment(start).format("DD/MM/YY HH:mm")}`,
            `<b>End:</b> ${moment(end).format("DD/MM/YY HH:mm")}`,
            `<b>Duration:</b> ${hrs}h ${mins}m`,
          ].join("<br/>"),
        }
      })
    )

    const garageItems = filteredData.flatMap((d) =>
      (d.garage_visits || []).filter((g) => g.entry_time).map((g, i) => {
        const start = new Date(g.entry_time)
        const end = g.exit_time ? new Date(g.exit_time) : new Date()
        const durMin = moment(end).diff(moment(start), "minutes")
        const hrs = Math.floor(durMin / 60)
        const mins = durMin % 60

        return {
          id: `${d.truck_no}-g-${i}`,
          group: d.truck_no,
          content: `<span style="font-size:11px;color:#b71c1c;font-weight:bold">&#9881; ${g.garage_name}</span>`,
          start,
          end,
          type: "range",
          style: `background-color:${GARAGE_COLOR};border-color:#e53935;`,
          title: [
            `<b>Garage:</b> ${g.garage_name} (${g.garage_code})`,
            `<b>Entry:</b> ${moment(start).format("DD/MM/YY HH:mm")}`,
            `<b>Exit:</b> ${g.exit_time ? moment(end).format("DD/MM/YY HH:mm") : "ยังอยู่ในอู่"}`,
            `<b>Duration:</b> ${hrs}h ${mins}m`,
          ].join("<br/>"),
        }
      })
    )

    const items = new DataSet([...historyItems, ...garageItems])

    const options = {
      width: "100%",
      height: "80vh",
      horizontalScroll: true,
      stack: true,
      showCurrentTime: true,
      orientation: { axis: "top", item: "top" },
      zoomMin: 1000 * 60 * 60,
      zoomMax: 1000 * 60 * 60 * 24 * 90,
      groupOrder: "id",
      margin: { item: { horizontal: 2, vertical: 3 } },
      tooltip: { followMouse: true, overflowMethod: "cap" },
      start: moment(value).startOf("month").toDate(),
      end: new Date(),
      min: moment(value).subtract(1, "month").startOf("month").toDate(),
      max: moment(value).add(1, "month").endOf("month").toDate(),
    }

    timelineRef.current = new Timeline(
      containerRef.current,
      items,
      groups,
      options
    )

    return () => {
      if (timelineRef.current) {
        timelineRef.current.destroy()
        timelineRef.current = null
      }
    }
  }, [data, value, selectedTrucks])

  const handleRemoveTruck = (truckNo) => {
    setSelectedTrucks((prev) => prev.filter((t) => t !== truckNo))
  }

  const truckGroups = useMemo(() => {
    const allTrucks = data.map((d) => d.truck_no)
    const groups = []
    for (let i = 0; i < allTrucks.length; i += 17) {
      groups.push(allTrucks.slice(i, i + 17))
    }
    return groups
  }, [data])

  const activeGroupIndex = useMemo(() => {
    if (selectedTrucks.length === 0) return "clear"
    if (selectedTrucks.length === data.length) return "all"
    const sorted = [...selectedTrucks].sort().join(",")
    return truckGroups.findIndex(
      (g) => [...g].sort().join(",") === sorted
    )
  }, [selectedTrucks, truckGroups, data.length])

  return (
    <div
      style={{
        height: "93vh",
        overflowY: "scroll",
        paddingLeft: "5px",
        paddingTop: "20px",
      }}
    >
      {/* Row 1: DatePicker + Truck filter + controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          marginBottom: "8px",
          flexWrap: "wrap",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            views={["year", "month"]}
            label="Year and Month"
            minDate={new Date("2022-02-01")}
            maxDate={new Date("2050-12-31")}
            value={value}
            onChange={(newValue) => setValue(newValue)}
            renderInput={(params) => <TextField {...params} helperText={null} />}
          />
        </LocalizationProvider>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={data.map((d) => d.truck_no)}
          value={selectedTrucks}
          onChange={(_, newValue) => setSelectedTrucks(newValue)}
          renderTags={() => null}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 4 }}
                checked={selected}
              />
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="รถที่แสดง"
              size="small"
              inputProps={{
                ...params.inputProps,
                value: "",
                placeholder:
                  selectedTrucks.length === data.length
                    ? `ทั้งหมด ${data.length} คัน`
                    : `เลือก ${selectedTrucks.length} / ${data.length} คัน`,
              }}
            />
          )}
          sx={{ width: 220 }}
          size="small"
        />
        <IconButton onClick={getData} disabled={isLoading} size="small">
          <RefreshIcon />
        </IconButton>
        {isLoading && <CircularProgress size={24} />}
        <span style={{ fontSize: "13px", color: "#666" }}>
          Scroll to zoom · Drag to pan
        </span>
      </div>

      {/* Row 2: Preset buttons */}
      {data.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "5px",
            marginBottom: "8px",
            alignItems: "center",
          }}
        >
          <Button
            size="small"
            variant={activeGroupIndex === "all" ? "contained" : "outlined"}
            onClick={() => setSelectedTrucks(data.map((d) => d.truck_no))}
            style={{ minWidth: 52, textTransform: "none", fontWeight: 600 }}
          >
            All
          </Button>
          <Button
            size="small"
            variant={activeGroupIndex === "clear" ? "contained" : "outlined"}
            color="error"
            onClick={() => setSelectedTrucks([])}
            style={{ minWidth: 52, textTransform: "none", fontWeight: 600 }}
          >
            Clear
          </Button>
          <span style={{ width: 1, height: 24, background: "#e0e0e0", margin: "0 2px" }} />
          {truckGroups.map((group, idx) => (
            <Button
              key={idx}
              size="small"
              variant={activeGroupIndex === idx ? "contained" : "outlined"}
              onClick={() => setSelectedTrucks(group)}
              style={{ minWidth: 36, textTransform: "none", fontWeight: 600 }}
            >
              {idx + 1}
            </Button>
          ))}
        </div>
      )}

      {/* Row 3: Selected truck chips */}
      {selectedTrucks.length > 0 && (
        <div
          style={{
            border: "1px solid #e0e0e0",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)",
            padding: "8px 12px",
            marginBottom: "8px",
            display: "flex",
            flexWrap: "wrap",
            gap: "5px",
            boxShadow: "inset 0 1px 3px rgba(0,0,0,0.06)",
          }}
        >
          {selectedTrucks.map((truckNo) => {
            const c = getTruckColor(truckNo)
            return (
              <Chip
                key={truckNo}
                label={truckNo}
                size="small"
                onDelete={() => handleRemoveTruck(truckNo)}
                style={{
                  backgroundColor: c.bg,
                  border: `1px solid ${c.border}`,
                  color: c.text,
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              />
            )
          })}
        </div>
      )}

      <h2 style={{ margin: "5px 0" }}>Truck Gantt Chart</h2>

      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "8px",
          fontSize: "13px",
        }}
      >
        {Object.entries(JOB_LABELS).map(([key, label]) => (
          <span
            key={key}
            style={{ display: "flex", alignItems: "center", gap: "4px" }}
          >
            <span
              style={{
                display: "inline-block",
                width: "14px",
                height: "14px",
                backgroundColor: key === "G" ? GARAGE_COLOR : JOB_COLORS[key],
                borderRadius: "3px",
              }}
            />
            {label}
          </span>
        ))}
      </div>

      <div ref={containerRef} />
    </div>
  )
}

export default ReportTruckGantt
